import { z } from "zod";

/**
 * Zod schemas for the LCP (Lancer Content Pack) format and the core Lancer
 * item types, written to match the *actual* shape of `@massif/lancer-data`
 * v4.0.5 (the core book in LCP shape), not the idealized COMP/CON model.
 *
 * **Altitude:** each schema strictly validates the load-bearing identity and
 * structure of an item (`id`, `name`, the fields the app resolves against) and
 * uses `.passthrough()` to carry the rich, deeply-nested optional content
 * (traits, actions, synergies, active_effects, …) verbatim. Fully modelling every
 * nested rules construct would brittle-reject valid community packs; tighten a
 * field only once the pilot/mech math actually reads it.
 */

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

/** A reference to a Tag *definition*, as embedded on items (not the definition itself). */
export const TagRefSchema = z
  .object({
    id: z.string(),
    val: z.union([z.string(), z.number()]).optional(),
  })
  .passthrough();
export type TagRef = z.infer<typeof TagRefSchema>;

/** Damage entries: `val` is a string ("2d6") on mech weapons, a number on pilot gear. */
export const DamageSchema = z
  .object({
    type: z.string(),
    val: z.union([z.string(), z.number()]),
  })
  .passthrough();
export type Damage = z.infer<typeof DamageSchema>;

/** Range entries. `val` may be a number or a dice/string expression. */
export const RangeSchema = z
  .object({
    type: z.string(),
    val: z.union([z.string(), z.number()]),
  })
  .passthrough();
export type Range = z.infer<typeof RangeSchema>;

// ---------------------------------------------------------------------------
// LCP manifest (lcp_manifest.json)
// ---------------------------------------------------------------------------

export const LcpDependencySchema = z
  .object({
    name: z.string(),
    version: z.string(),
    link: z.string().optional(),
  })
  .passthrough();
export type LcpDependency = z.infer<typeof LcpDependencySchema>;

export const LcpManifestSchema = z
  .object({
    name: z.string(),
    author: z.string(),
    version: z.string(),
    item_prefix: z.string().optional(),
    description: z.string().optional(),
    website: z.string().optional(),
    image_url: z.string().optional(),
    v3: z.boolean().optional(),
    dependencies: z.array(LcpDependencySchema).optional(),
  })
  .passthrough();
export type LcpManifest = z.infer<typeof LcpManifestSchema>;

// ---------------------------------------------------------------------------
// Core item types
// ---------------------------------------------------------------------------

/** Frame stats as shipped in lancer-data — flat keys, not nested. */
export const FrameStatsSchema = z
  .object({
    size: z.number(),
    armor: z.number(),
    hp: z.number(),
    evasion: z.number(),
    edef: z.number(),
    heatcap: z.number(),
    repcap: z.number(),
    sensor_range: z.number(),
    tech_attack: z.number(),
    save: z.number(),
    speed: z.number(),
    sp: z.number(),
    structure: z.number(),
    stress: z.number(),
  })
  .passthrough();
export type FrameStats = z.infer<typeof FrameStatsSchema>;

export const FrameSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    source: z.string().optional(),
    license_level: z.number().optional(),
    mechtype: z.array(z.string()).optional(),
    description: z.string().optional(),
    mounts: z.array(z.string()).optional(),
    stats: FrameStatsSchema,
  })
  .passthrough();
export type Frame = z.infer<typeof FrameSchema>;

export const WeaponSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    mount: z.string().optional(),
    type: z.string().optional(),
    source: z.string().optional(),
    license: z.string().optional(),
    license_level: z.number().optional(),
    damage: z.array(DamageSchema).optional(),
    range: z.array(RangeSchema).optional(),
    tags: z.array(TagRefSchema).optional(),
    sp: z.number().optional(),
    description: z.string().optional(),
    // Talent-exclusive gear: present on items granted by a pilot talent (e.g.
    // `mw_prototype_1`/Engineer, `mw_fuel_rod_gun`/Nuclear Cavalier). Rides through
    // at runtime via `.passthrough()`; these three fields surface it to TS for
    // `talentGear.ts`'s equip gate.
    talent_item: z.boolean().optional(),
    talent_id: z.string().optional(),
    talent_rank: z.number().optional(),
  })
  .passthrough();
export type Weapon = z.infer<typeof WeaponSchema>;

export const SystemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.string().optional(),
    source: z.string().optional(),
    license: z.string().optional(),
    license_level: z.number().optional(),
    sp: z.number().optional(),
    tags: z.array(TagRefSchema).optional(),
    effect: z.string().optional(),
    description: z.string().optional(),
    // Talent-exclusive gear: present on items granted by a pilot talent (e.g.
    // `ms_technophile_1`/Technophile, `ms_walking_armory_1`/Walking Armory). Rides
    // through at runtime via `.passthrough()`; these three fields surface it to TS
    // for `talentGear.ts`'s equip gate.
    talent_item: z.boolean().optional(),
    talent_id: z.string().optional(),
    talent_rank: z.number().optional(),
  })
  .passthrough();
export type System = z.infer<typeof SystemSchema>;

export const ModSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    source: z.string().optional(),
    license: z.string().optional(),
    license_level: z.number().optional(),
    sp: z.number().optional(),
    allowed_types: z.array(z.string()).optional(),
    effect: z.string().optional(),
    tags: z.array(TagRefSchema).optional(),
    description: z.string().optional(),
  })
  .passthrough();
export type Mod = z.infer<typeof ModSchema>;

export const TalentSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    icon: z.string().optional(),
    terse: z.string().optional(),
    description: z.string().optional(),
    ranks: z.array(z.object({ name: z.string() }).passthrough()),
  })
  .passthrough();
export type Talent = z.infer<typeof TalentSchema>;

export const SkillSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    detail: z.string().optional(),
  })
  .passthrough();
export type Skill = z.infer<typeof SkillSchema>;

/** A pilot origin. Backgrounds are narrative prompts; `skills` are suggestions, never grants. */
export const BackgroundSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    skills: z.array(z.string()).optional(),
  })
  .passthrough();
export type Background = z.infer<typeof BackgroundSchema>;

/** A Tag *definition* (from tags.json), distinct from a TagRef embedded on items. */
export const TagSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    filter_ignore: z.boolean().optional(),
    hidden: z.boolean().optional(),
  })
  .passthrough();
export type Tag = z.infer<typeof TagSchema>;

/**
 * License grouping (license_<name>.json in an LCP). Not present in core
 * lancer-data — licenses there are implied by frame `license`/`license_id`
 * fields — but community packs ship explicit license files.
 */
export const LicenseSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    source: z.string().optional(),
    unlocks: z.array(z.array(z.string())).optional(),
  })
  .passthrough();
export type License = z.infer<typeof LicenseSchema>;

export const PilotGearSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.string().optional(),
    description: z.string().optional(),
    tags: z.array(TagRefSchema).optional(),
    range: z.array(RangeSchema).optional(),
    damage: z.array(DamageSchema).optional(),
    // Pilot armor (type:"Armor") carries its numbers here, not in damage/range — a `pilot_*`
    // bonus id (see `@lancer-vtt/rules`' `applyBonus`/StatBonusId) with `val`/`replace`. Same
    // shape as `CoreBonusSchema.bonuses` below; typed (not left to `.passthrough()`) because the
    // compendium view needs `val`/`replace` to render a set-vs-add stat profile, not just carry
    // the field through verbatim.
    bonuses: z.array(z.object({ id: z.string(), val: z.union([z.string(), z.number()]), replace: z.boolean().optional(), overwrite: z.boolean().optional() }).passthrough()).optional(),
  })
  .passthrough();
export type PilotGear = z.infer<typeof PilotGearSchema>;

/**
 * A pilot **core bonus** (core_bonuses.json). `source` is the manufacturer
 * (GMS / IPS-N / SSC / HORUS / HA) — the field the core-bonus prerequisite gate
 * reads (per-manufacturer license-rank requirement; GMS is always available).
 * `bonuses` is the same id-keyed bonus array talents carry: those naming a mech
 * stat flow through the bonus engine (`compute.ts`). The richer
 * optional content (`actions`, `synergies`, `active_effects`, `mounted_effect`,
 * `integrated`) rides through verbatim via `.passthrough()`.
 */
export const CoreBonusSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    source: z.string().optional(),
    effect: z.string().optional(),
    description: z.string().optional(),
    mounted_effect: z.string().optional(),
    tags: z.array(TagRefSchema).optional(),
    bonuses: z.array(z.object({ id: z.string() }).passthrough()).optional(),
  })
  .passthrough();
export type CoreBonus = z.infer<typeof CoreBonusSchema>;

// ---------------------------------------------------------------------------
// NPC types — empty in core lancer-data v4.0.5 (shipped via @massif/lancer-npc-data).
// Schemas modelled from the COMP/CON spec; kept lenient so real NPC packs parse.
// ---------------------------------------------------------------------------

export const NpcClassSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.string().optional(),
    // `flavor`/`terse` are the player-facing public-capability prose RAW p.283
    // mandates be knowable at "class" tier (see npcDossier.ts); `tactics` is authored
    // to the GM and deliberately never rendered to a player.
    info: z
      .object({ flavor: z.string().optional(), tactics: z.string().optional(), terse: z.string().optional() })
      .passthrough()
      .optional(),
    stats: z.unknown().optional(),
    base_features: z.array(z.string()).optional(),
    optional_features: z.array(z.string()).optional(),
  })
  .passthrough();
export type NpcClass = z.infer<typeof NpcClassSchema>;

export const NpcTemplateSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    base_features: z.array(z.string()).optional(),
    optional_features: z.array(z.string()).optional(),
  })
  .passthrough();
export type NpcTemplate = z.infer<typeof NpcTemplateSchema>;

export const NpcFeatureSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.string().optional(),
    tags: z.array(TagRefSchema).optional(),
    description: z.string().optional(),
  })
  .passthrough();
export type NpcFeature = z.infer<typeof NpcFeatureSchema>;

/**
 * A **rollable table** (COMP/CON `RollableTable`, an LCP's `tables.json`): a die, and result
 * bands over it. The v3 NPC pack ships one, "Monstrosity Structure Damage" (`core-monstrosities`),
 * which a Monstrosity with Structure rolls instead of the mech Structure Damage table. A band
 * whose `min`/`max` are `-1` is the pack's encoding of the "multiple 1s" row.
 */
export const RollableTableSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string().optional(),
    die: z.number().optional(),
    results: z.array(
      z.object({ min: z.number(), max: z.number(), title: z.string().optional(), result: z.string() }).passthrough(),
    ),
  })
  .passthrough();
export type RollableTable = z.infer<typeof RollableTableSchema>;

// ---------------------------------------------------------------------------
// Item-type registry: the single source of truth tying a type key to its schema.
// ---------------------------------------------------------------------------

/**
 * Maps each compendium item type to (a) its zod schema and (b) the LCP JSON
 * filename it lives in. The Compendium index and the LCP parser both drive off
 * this registry, so adding a type is a one-line change here.
 */
export const ITEM_SCHEMAS = {
  frame: { schema: FrameSchema, file: "frames.json" },
  weapon: { schema: WeaponSchema, file: "weapons.json" },
  system: { schema: SystemSchema, file: "systems.json" },
  mod: { schema: ModSchema, file: "mods.json" },
  talent: { schema: TalentSchema, file: "talents.json" },
  skill: { schema: SkillSchema, file: "skills.json" },
  background: { schema: BackgroundSchema, file: "backgrounds.json" },
  tag: { schema: TagSchema, file: "tags.json" },
  license: { schema: LicenseSchema, file: "licenses.json" },
  pilot_gear: { schema: PilotGearSchema, file: "pilot_gear.json" },
  core_bonus: { schema: CoreBonusSchema, file: "core_bonuses.json" },
  npc_class: { schema: NpcClassSchema, file: "npc_classes.json" },
  npc_template: { schema: NpcTemplateSchema, file: "npc_templates.json" },
  npc_feature: { schema: NpcFeatureSchema, file: "npc_features.json" },
  rollable_table: { schema: RollableTableSchema, file: "tables.json" },
} as const satisfies Record<string, { schema: z.ZodType<{ id: string }>; file: string }>;

/** The set of compendium item-type keys (`"frame" | "weapon" | …`). */
export type ItemType = keyof typeof ITEM_SCHEMAS;

/** The validated TS type for a given item-type key. */
export type ItemOf<T extends ItemType> = z.infer<(typeof ITEM_SCHEMAS)[T]["schema"]>;

export const ITEM_TYPES = Object.keys(ITEM_SCHEMAS) as ItemType[];
